import type { PublicUserProfile } from "@fitties/shared";
import logger from "@logger";
import { BioMessageChain } from "@shared/components/BioMessageChain";

import clsx from "clsx";
import { Heart, Skull } from "lucide-react";
import type { RefObject } from "react";
import { getPresenceBadge, type PresenceBadge } from "../presenceBadge";
import { resolveDiscoveryUserId } from "../services/discoveryService";
import type { TinderCardAPI } from "../types";

const PRESENCE_BADGE_CONFIG: Record<PresenceBadge, { label: string; backgroundColor: string; testId: string }> = {
  here_tonight: {
    label: "Here tonight",
    backgroundColor: "var(--color-primary)",
    testId: "presence-badge-tonight",
  },
  here_this_week: {
    label: "Here this week",
    backgroundColor: "var(--color-secondary)",
    testId: "presence-badge-this-week",
  },
};

interface UserCardProps {
  user: PublicUserProfile;
  isActive: boolean;
  interactionLocked?: boolean;
  onSwipe: (direction: "left" | "right") => void;
  onMatch: (match: { userId: string; user: PublicUserProfile }) => void;
  tinderCardRef?: RefObject<TinderCardAPI | null> | TinderCardAPI | null;
}

/**
 * Discovery profile card: full-bleed photo with a bottom white gradient overlay
 * for name, bio, and swipe actions.
 */
export function UserCard({
  user,
  isActive,
  interactionLocked = false,
  onSwipe: _onSwipe,
  onMatch: _onMatch,
  tinderCardRef,
}: UserCardProps) {
  const triggerSwipe = (direction: "left" | "right") => {
    if (interactionLocked || !isActive || !tinderCardRef) {
      return;
    }

    const api = "current" in tinderCardRef ? tinderCardRef.current : tinderCardRef;

    if (!api) {
      logger.debug("TinderCard API not found", { tinderCardRef });
      return;
    }

    api.swipe(direction);
  };

  const userAge = user.age;
  const rowId = resolveDiscoveryUserId(user) ?? "";
  const presenceBadge = PRESENCE_BADGE_CONFIG[getPresenceBadge(user.lastKnownEventEnteredAt)];

  return (
    <div
      className={clsx(
        "absolute inset-0 overflow-hidden bg-white",
        isActive && !interactionLocked ? "cursor-grab touch-none active:cursor-grabbing" : "cursor-default",
        isActive ? "" : "pointer-events-none"
      )}
      data-testid="user-card"
      data-user-id={rowId}
      data-active={isActive}
    >
      <div className="absolute inset-0">
        {user.photo_url ? (
          <img
            src={user.photo_url}
            alt={`${user.display_name}'s profile`}
            className="h-full w-full object-cover"
            data-testid="user-photo"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-300">
            <span className="font-mono text-lg text-gray-600">No photo</span>
          </div>
        )}
      </div>

      <div
        className="user-card-photo-gradient pointer-events-none absolute inset-x-0 bottom-0 h-[min(48%,18rem)]"
        data-testid="user-card-gradient"
        aria-hidden
      />

      <div
        className="absolute inset-x-0 bottom-0 flex flex-col px-5 pb-[calc(2.75rem+1.5rem)] pt-8"
        data-testid="user-info"
      >
        <h2
          className={clsx(
            "shrink-0 font-mono font-bold leading-tight text-gray-900",
            "text-[length:clamp(1.75rem,6vw,2.25rem)]"
          )}
        >
          {user.display_name}
          {userAge != null && (
            <span className={clsx("ml-2 font-normal text-gray-600", "text-[length:clamp(1.375rem,5vw,1.875rem)]")}>
              {userAge}
            </span>
          )}
        </h2>

        <div
          className={clsx(
            "mt-2 max-h-[28dvh] shrink min-h-0 overflow-y-auto font-mono leading-relaxed text-gray-900",
            "text-[length:clamp(0.8125rem,3.2vw,1rem)]"
          )}
          data-testid="user-bio"
        >
          <BioMessageChain compact bio={user.bio} bioContent={user.bioContent} />
        </div>

        <div className="mt-4 flex shrink-0 justify-center gap-8">
          <button
            type="button"
            onClick={() => triggerSwipe("left")}
            disabled={!isActive || interactionLocked}
            className={clsx(
              "pressable flex size-16 items-center justify-center rounded-full shadow-lg transition-transform",
              isActive ? "cursor-pointer hover:scale-110" : "cursor-not-allowed opacity-50"
            )}
            style={{ backgroundColor: "var(--color-secondary)" }}
            data-testid="reject-button"
            aria-label="Pass on this user"
          >
            <Skull className="size-8 text-white" />
          </button>

          <button
            type="button"
            onClick={() => triggerSwipe("right")}
            disabled={!isActive || interactionLocked}
            className={clsx(
              "pressable flex size-16 items-center justify-center rounded-full shadow-lg transition-transform",
              isActive ? "cursor-pointer hover:scale-110" : "cursor-not-allowed opacity-50"
            )}
            style={{ backgroundColor: "var(--color-primary)" }}
            data-testid="like-button"
            aria-label="Like this user"
          >
            <Heart className="size-8 text-white" />
          </button>
        </div>
      </div>

      <div
        className="absolute inset-x-0 bottom-0 flex h-11 items-center justify-center"
        style={{ backgroundColor: presenceBadge.backgroundColor }}
        data-testid={presenceBadge.testId}
      >
        <span className="font-mono text-sm font-semibold uppercase tracking-wide text-white">
          {presenceBadge.label}
        </span>
      </div>
    </div>
  );
}
